import {
  BarChart3,
  Boxes,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  ClipboardList,
  Factory,
  FileSpreadsheet,
  FileText,
  Hammer,
  Home,
  Images,
  IndianRupee,
  LayoutGrid,
  LifeBuoy,
  List,
  type LucideIcon,
  Package,
  Receipt,
  Scissors,
  Settings,
  Shirt,
  ShoppingCart,
  Tags,
  TrendingUp,
  Truck,
  UserCheck,
  UserCog,
  Users,
  Wallet,
} from 'lucide-react';
import type { UserRole } from '@tailoring-crm/shared';

/**
 * The navigation is two things at once: a RAIL of sections down the left edge, an icon over a
 * label each, and beside it a COLUMN listing the screens inside whichever section is open. The
 * phone bar is a third, flatter view of the same list. One definition feeds all three, so a
 * screen cannot be reachable on the laptop and lost on the phone.
 *
 * A section is where the shop thinks of the work as happening: Orders is the counter, People
 * are who walks in, Workshop is the floor and its materials, Money is the till and the book,
 * Staff is the people who make things, Reports is the evening. Settings is its own.
 */
export type NavGroupKey =
  'home' | 'orders' | 'people' | 'workshop' | 'money' | 'staff' | 'reports' | 'settings' | 'work';

export interface NavGroup {
  key: NavGroupKey;
  labelKey: string;
  icon: LucideIcon;
}

export interface NavItem {
  to: string;
  labelKey: string;
  icon: LucideIcon;
  /** Which rail section this screen lives under. */
  group: NavGroupKey;
  end?: boolean;
  /**
   * The path the entry stands for when that is wider than where it goes. Orders goes to the
   * booking form but lights up for every order screen.
   */
  activeFor?: string;
  /**
   * A sub-path this entry should NOT light up for, because another entry owns it — for an entry
   * whose prefix covers a screen that has a nav entry of its own.
   */
  notFor?: string;
  /**
   * Laptop sidebar only, left out of the phone bar.
   *
   * The bar is at six items already and each one gets 60px at 360px — a seventh takes that to
   * 51, which is below a comfortable thumb and truncates every label. The sidebar has the room
   * and the phone does not, and nothing becomes unreachable: every screen is also linked from
   * the screens around it.
   */
  sidebarOnly?: boolean;
}

export const NAV_GROUPS: readonly NavGroup[] = Object.freeze([
  { key: 'home', labelKey: 'nav.groups.home', icon: Home },
  { key: 'work', labelKey: 'nav.groups.work', icon: Scissors },
  { key: 'orders', labelKey: 'nav.groups.orders', icon: ClipboardList },
  { key: 'people', labelKey: 'nav.groups.people', icon: Users },
  { key: 'workshop', labelKey: 'nav.groups.workshop', icon: Hammer },
  { key: 'money', labelKey: 'nav.groups.money', icon: IndianRupee },
  { key: 'staff', labelKey: 'nav.groups.staff', icon: UserCog },
  { key: 'reports', labelKey: 'nav.groups.reports', icon: BarChart3 },
  { key: 'settings', labelKey: 'nav.groups.settings', icon: Settings },
]);

export const PRIMARY_NAV: readonly NavItem[] = Object.freeze([
  { to: '/', labelKey: 'nav.dashboard', icon: Home, group: 'home', end: true },
  // The board is the second thing on the phone bar: it is what a shop opens in the morning and
  // taps all day. In the sidebar it lives with the rest of the floor.
  { to: '/board', labelKey: 'nav.board', icon: LayoutGrid, group: 'workshop' },
  {
    to: '/orders/new',
    activeFor: '/orders',
    labelKey: 'nav.orders',
    icon: ClipboardList,
    group: 'orders',
  },
  // The order book itself. Orders above opens the booking form, so without this the list was
  // only reachable through the form's back arrow.
  {
    to: '/orders',
    labelKey: 'nav.allOrders',
    icon: List,
    group: 'orders',
    end: true,
    sidebarOnly: true,
  },
  {
    to: '/quotations',
    labelKey: 'nav.quotations',
    icon: FileText,
    group: 'orders',
    sidebarOnly: true,
  },
  {
    to: '/appointments',
    labelKey: 'nav.appointments',
    icon: CalendarDays,
    group: 'orders',
    sidebarOnly: true,
  },
  {
    to: '/alterations',
    labelKey: 'nav.alterations',
    icon: Scissors,
    group: 'orders',
    sidebarOnly: true,
  },
  { to: '/customers', labelKey: 'nav.customers', icon: Users, group: 'people' },
  { to: '/gallery', labelKey: 'nav.gallery', icon: Images, group: 'people', sidebarOnly: true },
  {
    to: '/capacity',
    labelKey: 'nav.capacity',
    icon: CalendarClock,
    group: 'workshop',
    sidebarOnly: true,
  },
  { to: '/garments', labelKey: 'nav.garments', icon: Shirt, group: 'workshop', sidebarOnly: true },
  { to: '/products', labelKey: 'nav.products', icon: Tags, group: 'workshop', sidebarOnly: true },
  { to: '/stock', labelKey: 'nav.stock', icon: Boxes, group: 'workshop', sidebarOnly: true },
  {
    to: '/sourcing',
    labelKey: 'nav.sourcing',
    icon: Package,
    group: 'workshop',
    sidebarOnly: true,
  },
  {
    to: '/purchasing',
    labelKey: 'nav.purchasing',
    icon: ShoppingCart,
    group: 'workshop',
    sidebarOnly: true,
  },
  {
    to: '/outsourcing',
    labelKey: 'nav.outsourcing',
    icon: Truck,
    group: 'workshop',
    sidebarOnly: true,
  },
  {
    to: '/payments',
    labelKey: 'nav.payments',
    icon: IndianRupee,
    group: 'money',
    sidebarOnly: true,
  },
  { to: '/expenses', labelKey: 'nav.expenses', icon: Receipt, group: 'money', sidebarOnly: true },
  { to: '/payouts', labelKey: 'nav.payouts', icon: Wallet, group: 'money', sidebarOnly: true },
  { to: '/staff', labelKey: 'nav.staff', icon: UserCog, group: 'staff', sidebarOnly: true },
  {
    to: '/attendance',
    labelKey: 'nav.attendance',
    icon: CalendarCheck,
    group: 'staff',
    sidebarOnly: true,
  },
  {
    to: '/reports',
    labelKey: 'nav.reports',
    icon: BarChart3,
    group: 'reports',
    end: true,
    sidebarOnly: true,
  },
  // The eight reports, listed in the column so the evening's reading is one click from the rail.
  {
    to: '/reports/sales',
    labelKey: 'reports.sales.title',
    icon: TrendingUp,
    group: 'reports',
    sidebarOnly: true,
  },
  {
    to: '/reports/production',
    labelKey: 'reports.production.title',
    icon: Factory,
    group: 'reports',
    sidebarOnly: true,
  },
  {
    to: '/reports/staff',
    labelKey: 'reports.staff.title',
    icon: UserCheck,
    group: 'reports',
    sidebarOnly: true,
  },
  {
    to: '/reports/customers',
    labelKey: 'reports.customers.title',
    icon: Users,
    group: 'reports',
    sidebarOnly: true,
  },
  {
    to: '/reports/financial',
    labelKey: 'reports.financial.title',
    icon: Wallet,
    group: 'reports',
    sidebarOnly: true,
  },
  {
    to: '/reports/profit-and-loss',
    labelKey: 'reports.pnlLink',
    icon: BarChart3,
    group: 'reports',
    sidebarOnly: true,
  },
  {
    to: '/reports/sales-register',
    labelKey: 'reports.salesRegisterLink',
    icon: FileSpreadsheet,
    group: 'reports',
    sidebarOnly: true,
  },
  { to: '/settings', labelKey: 'nav.settings', icon: Settings, group: 'settings' },
  { to: '/support', labelKey: 'nav.support', icon: LifeBuoy, group: 'settings', sidebarOnly: true },
]);

export const TAILOR_NAV: readonly NavItem[] = Object.freeze([
  { to: '/my-work', labelKey: 'nav.myWork', icon: Scissors, group: 'work', end: true },
  { to: '/my-earnings', labelKey: 'nav.myEarnings', icon: Wallet, group: 'work' },
  { to: '/attendance', labelKey: 'nav.attendance', icon: CalendarCheck, group: 'staff' },
  { to: '/support', labelKey: 'nav.support', icon: LifeBuoy, group: 'settings' },
  { to: '/settings', labelKey: 'nav.settings', icon: Settings, group: 'settings' },
]);

export function isNavItemActive(item: NavItem, pathname: string): boolean {
  const base = item.activeFor ?? item.to;
  if (item.end) return pathname === base;
  return pathname === base || pathname.startsWith(`${base}/`);
}

/**
 * Whether an entry in the sidebar column reads as current. An entry whose address IS the page
 * wins outright, so the order book lights All orders alone rather than All orders and the
 * Orders entry that stands for every order screen.
 */
export function isSidebarItemActive(
  items: readonly NavItem[],
  item: NavItem,
  pathname: string,
): boolean {
  if (items.some((one) => one.to === pathname)) return item.to === pathname;
  return isNavItemActive(item, pathname) && !(item.notFor && pathname.startsWith(item.notFor));
}

export function navFor(role: UserRole | undefined): readonly NavItem[] {
  return role === 'tailor' ? TAILOR_NAV : PRIMARY_NAV;
}

export function bottomNavFor(role: UserRole | undefined): readonly NavItem[] {
  return navFor(role).filter((item) => !item.sidebarOnly);
}

/** The rail sections a role actually has screens in, in rail order. */
export function groupsFor(role: UserRole | undefined): readonly NavGroup[] {
  const items = navFor(role);
  return NAV_GROUPS.filter((group) => items.some((item) => item.group === group.key));
}

/** The screens inside one section, in the order they are listed. */
export function itemsInGroup(role: UserRole | undefined, key: NavGroupKey): readonly NavItem[] {
  return navFor(role).filter((item) => item.group === key);
}

/**
 * Whether the open section earns a column beside the rail. A section with one screen does not:
 * the column would repeat the rail's own label above a single link — Home, under HOME, beside
 * Home — and spend a fifth of the window saying so.
 */
export function hasSectionColumn(role: UserRole | undefined, pathname: string): boolean {
  return itemsInGroup(role, activeGroupFor(role, pathname)).length > 1;
}

/**
 * Which section is open for a path. The item that lights up decides; a path nothing claims
 * (a settings sub-page reached from the hub, say) falls back to the longest matching prefix,
 * and then to Home.
 */
export function activeGroupFor(role: UserRole | undefined, pathname: string): NavGroupKey {
  const items = navFor(role);
  const lit = items.find(
    (item) => isNavItemActive(item, pathname) && !(item.notFor && pathname.startsWith(item.notFor)),
  );
  if (lit) return lit.group;
  const byPrefix = [...items]
    .filter((item) => pathname.startsWith(item.to.split('/').slice(0, 2).join('/')))
    .sort((a, b) => b.to.length - a.to.length)[0];
  return byPrefix?.group ?? items[0]?.group ?? 'home';
}
